import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { marketsForScope } from '../dataProvider/types';
import type { PageSchema } from '../dataProvider';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  currentPublished,
  pageLifecycle,
} from './pageSchemaLifecycle';

/**
 * Page schemas list (D-page-schema-editor-8a / 8b). The authored pages by pageId,
 * market and DERIVED lifecycle (draft-only / published / draft-ahead, the content-
 * equality derivation, 8b), with the block count and the last publish time.
 * OPERATIONAL market-scoped: the switcher governs which schemas show (a uk editor
 * sees uk drafts). ROLE-gated to editor and super_admin (a capability read-gate would
 * leak via a viewer's '*:read'). Authoring a new draft gates pages:author; publish /
 * rollback are taken from the edit view (8b).
 */

const pageFilters = [
  <TextInput key="q" source="q" label="Search page id" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'draft', name: 'Draft' },
      { id: 'published', name: 'Published' },
    ]}
  />,
];

const PageSchemaListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'pages:author' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canAuthor && <CreateButton label="New page draft" />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const PageSchemaListView = () => {
  const { activeRegion } = useRegion();
  const markets = marketsForScope(activeRegion ?? 'GLOBAL');
  return (
    <List
      resource="pageSchemas"
      title="Page schemas"
      filter={{ market: markets }}
      filters={pageFilters}
      actions={<PageSchemaListActions />}
      sort={{ field: 'pageId', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="pageId" label="Page" />
        <FunctionField
          label="Market"
          render={(r: PageSchema) => String(r.market).toUpperCase()}
        />
        <FunctionField
          label="Status"
          render={(r: PageSchema) => {
            const state = pageLifecycle(r);
            return (
              <Chip
                size="small"
                variant={state === 'published_clean' ? 'filled' : 'outlined'}
                color={LIFECYCLE_COLOR[state]}
                label={LIFECYCLE_LABEL[state]}
                data-testid={`page-status-${r.id}`}
              />
            );
          }}
        />
        <FunctionField
          label="Blocks"
          render={(r: PageSchema) => r.blocks.length}
        />
        <FunctionField
          label="Last published"
          render={(r: PageSchema) =>
            currentPublished(r)?.publishedAt?.slice(0, 10) ?? 'never'
          }
        />
      </Datagrid>
    </List>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const PageSchemaList = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <PageSchemaListView />
  </RequireAccess>
);
